import { usePrevious } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { useStytch, useStytchSession, useStytchUser } from "@stytch/react";
import { useQuery } from "@tanstack/react-query";
import {
  signIn,
  useGetPathAfterLogin,
  useIsLoggedIn,
} from "features/auth/AuthUtils";
import {
  getStytchErrorMessage,
  getStytchSessionDurationMinutes,
  isStytchMultiFactorLoginNeeded,
  isStytchMultiFactorSetupNeeded,
  isStytchPasswordLoginNeeded,
  isStytchPasswordSetupNeeded,
  useStytchToken,
} from "features/auth/StytchUtils";
import { useTokenStytchCreate } from "gen/endpoints/token/token";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { LoadingState } from "shared/components/empty/LoadingState";
import { useToast } from "shared/components/toast/useToast";
import { queryOptionsSingleShot } from "shared/utils/QueryUtils";
import invariant from "shared/utils/TinyInvariant";

export const AuthContinuePage: React.FC = () => {
  const navigate = useNavigate();
  const { showErrorToast } = useToast();
  const { t } = useTranslation(["common", "auth"]);

  const isLoggedIn = useIsLoggedIn();
  const token = useStytchToken();
  const stytchClient = useStytch();

  const stytchUser = useStytchUser_safe();
  const stytchSession = useStytchSession_safe();

  const { error } = useQuery({
    queryKey: ["auth", "stytch", "session", token],
    queryFn: async () => {
      await stytchClient.magicLinks.authenticate(token, {
        session_duration_minutes: getStytchSessionDurationMinutes(),
      });
      return true;
    },
    ...queryOptionsSingleShot,
    enabled: !!token,
    throwOnError: false,
  });

  const prevError = usePrevious(error);

  useEffect(() => {
    if (error && !prevError) {
      showErrorToast(getStytchErrorMessage(t, error));
      navigate("/auth/login");
    }
  }, [error, navigate, prevError, showErrorToast, t]);

  // reauthenticate stytch
  if (!stytchUser || !stytchSession) {
    if (isLoggedIn) {
      return <Navigate to="/auth/logout" replace={true} />;
    }
    return <Navigate to="/auth/login" replace={true} />;
  }

  return <LoginContinueWithStytch />;
};

const LoginContinueWithStytch: React.FC = () => {
  const navigate = useNavigate();
  const { showErrorToast } = useToast();

  const stytchClient = useStytch();
  const isLoggedIn = useIsLoggedIn();
  const getPathAfterLogin = useGetPathAfterLogin();

  const stytchUser = useStytchUser_safe();
  const stytchSession = useStytchSession_safe();

  invariant(stytchUser, "Stytch user must be defined");
  invariant(stytchSession, "Stytch session must be defined");

  const needsPasswordSetup = isStytchPasswordSetupNeeded(stytchUser);
  const needsPasswordLogin = isStytchPasswordLoginNeeded(stytchSession);
  const needsMultiFactorSetup = isStytchMultiFactorSetupNeeded(stytchUser);
  const needsMultiFactorLogin = isStytchMultiFactorLoginNeeded(stytchSession);

  const isReady =
    !needsPasswordSetup &&
    !needsPasswordLogin &&
    !needsMultiFactorSetup &&
    !needsMultiFactorLogin;
  const prevIsReady = usePrevious(isReady);

  const didSignInRef = useRef(false);

  const { mutateAsync: signInWithStytch } = useTokenStytchCreate();

  useEffect(() => {
    const doSignIn = async () => {
      try {
        const data = await signInWithStytch({
          data: {
            stytch_token: stytchClient.session.getTokens()!.session_jwt,
          },
        });
        await signIn(data);
      } catch (e) {
        showErrorToast(e);
        // We thought the Stytch token was valid, but it was rejected by the backend.
        // This is probably due to clock skew and shouldn't happen often.
        // Redirect to password page so the user can try again without clearing
        // the "remember for 30 days" extended session.
        Sentry.addBreadcrumb({
          message: "Stytch token rejected by backend",
          level: "warning",
        });
        navigate("/auth/password", { replace: true });
      }
    };
    // When everything is ready (and we're logged out),
    // exchange the stytch token for our token and log in.
    // prevIsReady is checked to ensure this only happens once.
    if (!didSignInRef.current && !isLoggedIn && !prevIsReady && isReady) {
      didSignInRef.current = true;
      void doSignIn();
    }
  }, [
    isLoggedIn,
    isReady,
    navigate,
    prevIsReady,
    stytchClient,
    showErrorToast,
    signInWithStytch,
  ]);

  if (needsPasswordSetup) {
    return <Navigate to="/auth/password/setup" replace={true} />;
  }
  if (needsPasswordLogin) {
    return <Navigate to="/auth/password" replace={true} />;
  }
  if (needsMultiFactorSetup) {
    return <Navigate to="/auth/mfa/setup" replace={true} />;
  }
  if (needsMultiFactorLogin) {
    return <Navigate to="/auth/mfa" replace={true} />;
  }
  if (isLoggedIn) {
    return <Navigate to={getPathAfterLogin()} replace={true} />;
  }
  return <LoadingState />;
};

// NOTE: Not using result from useStytchUser/useStytchSession because they
// are not updating after successful webauthn authentication.

const useStytchUser_safe = () => {
  void useStytchUser();
  const stytchClient = useStytch();
  return useMemo(() => stytchClient.user.getSync(), [stytchClient]);
};

const useStytchSession_safe = () => {
  void useStytchSession();
  const stytchClient = useStytch();
  return useMemo(() => stytchClient.session.getSync(), [stytchClient]);
};
